import { COVERME_ROUTES } from '@trip-ux/constants';
import { get, isEmpty, set } from 'lodash';

// Type for runtime New Relic config injected by server
interface NewRelicConfig {
  init: Record<string, unknown>;
  info: {
    beacon: string;
    errorBeacon: string;
    licenseKey: string;
    applicationID: string;
    sa: number;
  };
  loader_config: {
    accountID: string;
    trustKey: string;
    agentID: string;
    licenseKey: string;
    applicationID: string;
  };
}

declare global {
  interface Window {
    __NEW_RELIC_CONFIG__?: NewRelicConfig;
  }

  interface GlobalThis {
    __NEW_RELIC_CONFIG__?: NewRelicConfig;
  }
}

export const createInitialStore = (state = {}, reminderInSeconds: number): Record<string, any> =>
  set(isEmpty(state) ? {} : state, 'keepAlive.reminderInSeconds', reminderInSeconds);

export const getLocaleRoutes = store => {
  const locale = get(store, 'config.locale') as string;
  const context = get(store, 'config.options.clientContextPath', {});
  const contextPath = context[locale];
  const routes = COVERME_ROUTES[locale];
  return {
    contextPath,
    routes
  };
};

export const updateConfigLang = newLocale => {
  const reduxStoreSerialized = sessionStorage.getItem('persist:redux-store');
  if (reduxStoreSerialized) {
    const reduxStore = JSON.parse(reduxStoreSerialized);
    if (reduxStore.config) {
      const config = JSON.parse(reduxStore.config);
      config.locale = newLocale;
      reduxStore.config = JSON.stringify(config);
    }
    const updatedReduxStoreSerialized = JSON.stringify(reduxStore);
    sessionStorage.setItem('persist:redux-store', updatedReduxStoreSerialized);
  }
};

let newRelicInitialized = false;
let newRelicInitialization: Promise<void> | null = null;

export const initializeNewRelic = (): void => {
  if (globalThis.window === undefined) {
    return;
  }

  if (newRelicInitialized || newRelicInitialization) {
    return;
  }

  // Use runtime config injected by server via template
  const nrConfig = globalThis.__NEW_RELIC_CONFIG__;

  if (!nrConfig) {
    return;
  }

  // Validate required configuration fields
  const { info } = nrConfig;
  if (!info?.licenseKey || !info?.applicationID) {
    return;
  }

  newRelicInitialization = import('@newrelic/browser-agent/loaders/browser-agent')
    .then(({ BrowserAgent }) => {
      new BrowserAgent(nrConfig);
      newRelicInitialized = true;
      console.info('[New Relic] Browser agent initialized successfully');
    })
    .catch(() => {
      // intentionally ignored - New Relic failure should not impact application behaviour
    })
    .finally(() => {
      newRelicInitialization = null;
    });
};
